import { useEffect, useMemo, useRef, useState } from "react";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import {
  COUNTRY_DIAL_CODES,
  parsePhoneValue,
  formatPhoneStored,
  type CountryDialCode,
} from "../../constants/countryDialCodes";

const E164_MAX_DIGITS = 15;

export interface PhoneInputWithCountryProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  placeholder?: string;
  className?: string;
  label?: string;
}

/** Filter by search query; when empty returns full list sorted by name. */
function getFilteredCountries(query: string): CountryDialCode[] {
  const q = query.trim().toLowerCase();
  const list =
    q.length === 0
      ? [...COUNTRY_DIAL_CODES]
      : COUNTRY_DIAL_CODES.filter(
          (c) =>
            c.name.toLowerCase().includes(q) ||
            c.dial.includes(q) ||
            c.dialDigits.includes(q) ||
            c.code.toLowerCase().includes(q)
        );
  return list.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Phone input with searchable country selector. Uses Headless UI Listbox.
 * Full country list is shown when opened; optional search filters the list.
 */
export function PhoneInputWithCountry({
  value,
  onChange,
  onBlur,
  error,
  required,
  disabled,
  id = "phone",
  placeholder = "555 123 456",
  className = "w-full",
  label = "Phone",
}: PhoneInputWithCountryProps) {
  const parsed = parsePhoneValue(value);
  const [country, setCountry] = useState<CountryDialCode>(parsed.country);
  const [nationalNumber, setNationalNumber] = useState(parsed.nationalNumber);
  const [search, setSearch] = useState("");
  const [lengthError, setLengthError] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const maxNationalLength = E164_MAX_DIGITS - country.dialDigits.length;
  const filteredList = useMemo(() => getFilteredCountries(search), [search]);
  const hasSearch = search.trim().length > 0;
  const showNoResults = hasSearch && filteredList.length === 0;

  useEffect(() => {
    const next = parsePhoneValue(value);
    setCountry(next.country);
    setNationalNumber(next.nationalNumber);
  }, [value]);

  const handleCountrySelect = (c: CountryDialCode) => {
    setCountry(c);
    setSearch("");
    const maxLen = E164_MAX_DIGITS - c.dialDigits.length;
    const trimmed = nationalNumber.slice(0, maxLen);
    setNationalNumber(trimmed);
    onChange(formatPhoneStored(c.dial, trimmed));
    setLengthError(null);
  };

  const handleNationalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "");
    const maxLen = E164_MAX_DIGITS - country.dialDigits.length;
    const v = digits.slice(0, maxLen);
    setNationalNumber(v);
    onChange(formatPhoneStored(country.dial, v));
    setLengthError(
      digits.length > maxLen ? `Max ${E164_MAX_DIGITS} digits total (${maxLen} for this country)` : null
    );
  };

  const displayError = error || lengthError;

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1"
        >
          {label}
          {required && <span className="text-red-500 ms-1">*</span>}
        </label>
      )}
      <div className="flex rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 overflow-hidden">
        <Listbox
          value={country}
          onChange={handleCountrySelect}
          disabled={disabled}
          as="div"
          className="relative z-10 flex-shrink-0"
        >
          <ListboxButton
            type="button"
            className="relative flex items-center gap-2 w-full ps-3 pe-9 py-3 min-w-[140px] max-w-[200px] bg-neutral-50 dark:bg-neutral-800 border-e border-neutral-300 dark:border-neutral-600 text-neutral-900 dark:text-neutral-100 text-left text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 cursor-pointer pointer-events-auto ui-disabled:opacity-50 ui-disabled:cursor-not-allowed"
          >
            <span className="truncate">
              {country.dial} {country.name}
            </span>
            <ChevronDownIcon className="w-4 h-4 flex-shrink-0 text-neutral-400 pointer-events-none absolute end-2 top-1/2 -translate-y-1/2" />
          </ListboxButton>
          <ListboxOptions
            anchor="bottom start"
            className="absolute start-0 top-full z-[100] mt-1 w-80 rounded-xl border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 shadow-xl overflow-hidden focus:outline-none data-[closed]:pointer-events-none"
          >
            {/* Search: filters the list; click/focus on input is allowed so it's editable */}
            <div
              className="p-2 border-b border-neutral-100 dark:border-neutral-700 bg-white dark:bg-neutral-800"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 rounded-lg bg-neutral-100 dark:bg-neutral-700 px-3 py-2.5">
                <MagnifyingGlassIcon className="w-4 h-4 text-neutral-400 flex-shrink-0" aria-hidden />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.preventDefault();
                    e.stopPropagation();
                  }}
                  placeholder="Search: Algeria, +33, FR..."
                  autoComplete="off"
                  aria-label="Search country"
                  className="flex-1 min-w-0 bg-transparent border-0 py-0.5 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                />
              </div>
            </div>
            <div className="max-h-56 overflow-y-auto py-1">
              {showNoResults && (
                <p className="px-4 py-3 text-sm text-neutral-500 dark:text-neutral-400">
                  No country found for &quot;{search.trim()}&quot;
                </p>
              )}
              {!showNoResults &&
                filteredList.map((c) => (
                  <ListboxOption
                    key={c.code}
                    value={c}
                    className="group cursor-default focus:outline-none"
                  >
                    {({ focus, selected }) => (
                      <div
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm ${
                          focus ? "bg-neutral-100 dark:bg-neutral-700" : ""
                        } ${selected ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300" : "text-neutral-800 dark:text-neutral-200"}`}
                      >
                        <span className="w-11 flex-shrink-0 font-mono text-neutral-500 dark:text-neutral-400 tabular-nums">
                          {c.dial}
                        </span>
                        <span className="font-medium">{c.name}</span>
                      </div>
                    )}
                  </ListboxOption>
                ))}
            </div>
          </ListboxOptions>
        </Listbox>
        <input
          id={id}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          dir="ltr"
          placeholder={placeholder}
          value={nationalNumber}
          onChange={handleNationalChange}
          onBlur={onBlur}
          disabled={disabled}
          maxLength={maxNationalLength}
          className="relative z-0 flex-1 min-w-0 border-0 bg-transparent px-4 py-3 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:outline-none focus:ring-0 text-base"
          aria-invalid={!!displayError}
          aria-describedby={displayError ? `${id}-error` : undefined}
        />
      </div>
      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
        If you have multiple phone numbers, add them in the notes field.
      </p>
      {displayError && (
        <p id={`${id}-error`} className="text-sm text-red-600 dark:text-red-400 mt-1" role="alert">
          {displayError}
        </p>
      )}
    </div>
  );
}
